import * as D from "@radix-ui/react-dialog"
import { cn, tv } from "@utils/tw"
import { ComponentProps, PropsWithChildren, ReactNode } from "react"

export type AlertDialogProps = {
  title?: ReactNode
  description?: ReactNode
  className?: string
  onSubmit?: () => void
  onDismiss?: (e: Event) => void
  open?: boolean
  padded?: boolean
  onOpenChange?: (open: boolean) => void
  /**
   * The trigger element that will open the dialog when clicked.
   */
  trigger?: ReactNode
  /** The contents of the dialog */
  children?: ReactNode
  /** Used to force mounting when more control is needed. Useful when controlling animation with React animation libraries. */
  forceMount?: boolean
}

export function Dialog(props: AlertDialogProps) {
  const styles = alertStyles({
    padded: props.padded === false ? false : true,
  })

  return (
    <D.Root
      open={props.open}
      onOpenChange={(open) => {
        props.onOpenChange?.(open)
      }}
    >
      {props.trigger && <D.Trigger asChild>{props.trigger}</D.Trigger>}
      <D.Portal forceMount={props.forceMount ? true : undefined}>
        <D.Overlay className={styles.overlay()} />
        <D.Content
          forceMount={props.forceMount ? true : undefined}
          className={styles.content({ className: props.className })}
          // onEscapeKeyDown={(e) => {
          //   requestCancel(e)
          // }}
          asChild
        >
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (props.onSubmit) {
                props.onSubmit()
              }
            }}
          >
            {props.title && <D.Title className={styles.title()}>{props.title}</D.Title>}
            {props.description && <D.Description className={styles.description()}>{props.description}</D.Description>}
            {props.children}
          </form>
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}

Dialog.Title = (props: ComponentProps<typeof D.Title>) => {
  const styles = alertStyles({})
  return (
    <D.Title {...props} className={cn(styles.title(), props.className)}>
      {props.children}
    </D.Title>
  )
}
Dialog.Description = (props: ComponentProps<typeof D.Description>) => {
  const styles = alertStyles({})
  return (
    <D.Description {...props} className={cn(styles.description(), props.className)}>
      {props.children}
    </D.Description>
  )
}
Dialog.Close = D.Close
Dialog.Header = (props: PropsWithChildren<{ className?: string }>) => {
  const styles = alertStyles({})
  return <div className={cn(styles.header(), props.className)}>{props.children}</div>
}
Dialog.Scroller = (props: PropsWithChildren<{ className?: string; padded?: boolean }>) => {
  const styles = alertStyles({})
  return (
    <div className={cn(styles.inner(), props.className, "asdf", props.padded === false ? "p-0" : "")}>
      {props.children}
    </div>
  )
}
Dialog.Footer = (props: PropsWithChildren<{ className?: string }>) => {
  const styles = alertStyles({})
  return <div className={cn(styles.footer(), props.className)}>{props.children}</div>
}

const alertStyles = tv({
  slots: {
    overlay: "radix-backdrop",
    content: [
      "radix-modal-animation @container-normal",
      "fixed z-50 left-1/2 top-1/2 max-h-[85vh] max-w-[90vw] -translate-x-1/2 -translate-y-1/2",
      "p-4 bg-white shadow-md focus:outline-none",
      "flex flex-col",
    ],
    title: "type-title-s",
    description: "type-body",
    buttons: "flex justify-end gap-[25px]",
    header: "py-4 px-5 flex-none",
    inner: "p-5 border-t border-grey2 overflow-auto flex-1",
    footer: "px-4 py-3 border-t border-grey2 flex-none flex justify-end",
  },
  variants: {
    padded: {
      true: {
        content: "p-5",
      },
      false: {
        content: "p-0",
      },
    },
  },
})
